<script setup lang="ts">
import { WuiInputSwitch } from '@wui/common-library'
import { computed } from 'vue'

import { useBackgroundBlurPreference } from '../model/use-background-blur-preference'

defineOptions({ name: 'BackgroundBlurControls' })

withDefaults(defineProps<{
  /** Compact dark layout for in-call footer. */
  compact?: boolean
}>(), {
  compact: false,
})

const {
  isSupported,
  isEnabled,
  strength,
  strengthMin,
  strengthMax,
  strengthStep,
} = useBackgroundBlurPreference()

const strengthPercent = computed(() =>
  `${((strength.value - strengthMin) / (strengthMax - strengthMin)) * 100}%`,
)

const onStrengthInput = (event: Event) => {
  strength.value = Number((event.target as HTMLInputElement).value)
}
</script>

<template>
  <div
    v-if="isSupported"
    :class="compact ? 'flex flex-col gap-2 text-fg-base-fg3' : 'flex flex-col gap-4'"
    data-test="background-blur-controls"
  >
    <div
      :class="compact
        ? 'flex items-center'
        : 'grid grid-cols-[80px_384px] tablet:grid-cols-[1fr_3fr] gap-x-4 items-center'"
    >
      <p
        v-if="!compact"
        class="text-1324 font-light"
      >
        {{ $t('soundAndVideo.backgroundBlur') }}
      </p>
      <label
        v-else
        class="text-1316"
      >
        {{ $t('soundAndVideo.backgroundBlur') }}
      </label>
      <wui-input-switch
        v-model="isEnabled"
        :small="compact"
        :class="compact ? 'ml-auto w-7' : ''"
      />
    </div>
    <div
      v-if="isEnabled"
      :class="compact
        ? 'flex flex-col gap-1'
        : 'grid grid-cols-[80px_384px] tablet:grid-cols-[1fr_3fr] gap-x-4 items-center'"
    >
      <p
        v-if="!compact"
        class="text-1324 font-light"
      >
        {{ $t('soundAndVideo.backgroundBlurStrength') }}
      </p>
      <label
        v-else
        class="text-1316 mb-1"
      >
        {{ $t('soundAndVideo.backgroundBlurStrength') }}
      </label>
      <input
        type="range"
        :min="strengthMin"
        :max="strengthMax"
        :step="strengthStep"
        :value="strength"
        class="background-blur-strength w-full"
        :style="{ backgroundSize: strengthPercent }"
        @input="onStrengthInput"
      >
    </div>
  </div>
</template>

<style scoped>
.background-blur-strength {
  appearance: none;
  height: 4px;
  border-radius: 9999px;
  background: color-mix(in srgb, currentColor 24%, transparent);
  background-image: linear-gradient(currentColor, currentColor);
  background-repeat: no-repeat;
}

.background-blur-strength::-webkit-slider-thumb {
  appearance: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: currentColor;
  cursor: pointer;
}

.background-blur-strength::-moz-range-thumb {
  width: 14px;
  height: 14px;
  border: none;
  border-radius: 50%;
  background: currentColor;
  cursor: pointer;
}
</style>
